import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Layers, X } from "lucide-react";
import { toast } from "sonner";
import { api, fmtDate, fmtTL, ORDER_STATUS } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Badge } from "../components/ui/badge";
import { Button } from "../components/ui/button";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";

const FILTERS = [
    { key: "all", label: "Tümü" },
    { key: "pending", label: "Onay Bekliyor" },
    { key: "confirmed", label: "Onaylandı" },
    { key: "preparing", label: "Hazırlanıyor" },
    { key: "shipped", label: "Kargoda" },
    { key: "delivered", label: "Teslim Edildi" },
    { key: "cancelled", label: "İptal" },
];

const SHIP_METHODS = [["own_vehicle", "Firmamız Araçları"], ["cargo", "Kargo"], ["freight", "Nakliye"], ["other", "Diğer"]];
const BULK_STATUSES = [["confirmed", "Onayla"], ["preparing", "Hazırlamaya Al"], ["shipped", "Kargoya Ver"]];

const deliveryProgress = (o) => {
    const total = o.items.reduce((a, i) => a + i.qty, 0);
    const done = o.items.reduce((a, i) => a + (i.delivered_qty || 0), 0);
    return total ? Math.round((done / total) * 100) : 0;
};

export default function Orders() {
    const { user } = useAuth();
    const [orders, setOrders] = useState(null);
    const [filter, setFilter] = useState("all");
    const [selected, setSelected] = useState(new Set());
    const [bulkStatus, setBulkStatus] = useState("confirmed");
    const [busy, setBusy] = useState(false);
    const [shipDlg, setShipDlg] = useState(false);
    const [shipMethod, setShipMethod] = useState("");
    const [shipFields, setShipFields] = useState({});
    const isSupplier = user?.role === "supplier";

    const load = () => api.get("/orders").then((r) => setOrders(r.data)).catch(() => {});
    useEffect(() => { load(); }, []);

    const filtered = (orders || []).filter((o) => filter === "all" || o.status === filter);
    const colSpan = isSupplier ? 9 : 8;

    const toggle = (id) => setSelected((s) => { const n = new Set(s); if (n.has(id)) { n.delete(id); } else { n.add(id); } return n; });
    const toggleAll = () => setSelected((s) => (s.size === filtered.length ? new Set() : new Set(filtered.map((o) => o.order_id))));

    const applyBulk = async (extra = {}) => {
        setBusy(true);
        try {
            const r = await api.post("/orders/bulk-status", { order_ids: [...selected], status: bulkStatus, ...extra });
            const { updated, failed } = r.data;
            if (updated.length) toast.success(`${updated.length} sipariş güncellendi: ${updated.join(", ")}`);
            failed.forEach((f) => toast.error(f.reason));
            setSelected(new Set());
            setShipDlg(false);
            setShipMethod("");
            setShipFields({});
            load();
        } catch (e) {
            toast.error(e.response?.data?.detail || "Toplu güncelleme başarısız");
        } finally {
            setBusy(false);
        }
    };

    const onApplyClick = () => {
        if (bulkStatus === "shipped") { setShipDlg(true); return; }
        applyBulk();
    };

    return (
        <div className="space-y-6" data-testid="orders-page">
            <div>
                <h1 className="font-display text-3xl font-bold tracking-tight">{isSupplier ? "Gelen Siparişler" : "Siparişlerim"}</h1>
                <p className="mt-1 text-sm text-muted-foreground">{filtered.length} sipariş</p>
            </div>

            <div className="flex gap-2 overflow-x-auto pb-1 scrollbar-thin" data-testid="order-status-filters">
                {FILTERS.map((f) => (
                    <button
                        key={f.key}
                        onClick={() => setFilter(f.key)}
                        className={`shrink-0 rounded-full border px-3.5 py-1.5 text-xs font-medium transition-colors ${filter === f.key ? "border-primary bg-primary text-primary-foreground" : "hover:border-terra-500/50"}`}
                        data-testid={`order-filter-${f.key}`}
                    >
                        {f.label}
                    </button>
                ))}
            </div>

            {isSupplier && selected.size > 0 && (
                <div className="flex flex-wrap items-center gap-3 rounded-2xl border border-terra-200 bg-terra-50 p-4" data-testid="bulk-action-bar">
                    <Layers className="h-4 w-4 text-terra-600" />
                    <p className="text-sm font-semibold" data-testid="bulk-selected-count">{selected.size} sipariş seçildi</p>
                    <select value={bulkStatus} onChange={(e) => setBulkStatus(e.target.value)} className="h-9 rounded-md border bg-background px-3 text-sm" data-testid="bulk-status-select">
                        {BULK_STATUSES.map(([k, l]) => <option key={k} value={k}>{l}</option>)}
                    </select>
                    <Button size="sm" disabled={busy} onClick={onApplyClick} className="bg-terra-600 text-white hover:bg-terra-700" data-testid="bulk-apply-button">{busy ? "Uygulanıyor..." : "Toplu Uygula"}</Button>
                    <Button size="sm" variant="outline" onClick={() => setSelected(new Set())} data-testid="bulk-clear-button"><X className="mr-1 h-3.5 w-3.5" /> Seçimi Temizle</Button>
                </div>
            )}

            <Dialog open={shipDlg} onOpenChange={setShipDlg}>
                <DialogContent data-testid="bulk-ship-dialog">
                    <DialogHeader>
                        <DialogTitle className="font-display">Toplu Gönderim — {selected.size} sipariş</DialogTitle>
                    </DialogHeader>
                    <div className="space-y-3">
                        <select className="h-10 w-full rounded-md border bg-background px-3 text-sm" value={shipMethod} onChange={(e) => { setShipMethod(e.target.value); setShipFields({}); }} data-testid="bulk-ship-method-select">
                            <option value="">Gönderim şekli seçin *</option>
                            {SHIP_METHODS.map(([k, l]) => <option key={k} value={k}>{l}</option>)}
                        </select>
                        {shipMethod === "cargo" && (
                            <>
                                <Input placeholder="Kargo firması * (örn: Aras Kargo)" value={shipFields.cargo_firm || ""} onChange={(e) => setShipFields({ ...shipFields, cargo_firm: e.target.value })} data-testid="bulk-ship-cargo-firm" />
                                <Input placeholder="Kargo takip numarası *" value={shipFields.tracking_no || ""} onChange={(e) => setShipFields({ ...shipFields, tracking_no: e.target.value })} data-testid="bulk-ship-tracking-no" />
                            </>
                        )}
                        {shipMethod === "freight" && (
                            <>
                                <Input placeholder="Nakliyeci adı soyadı *" value={shipFields.freight_name || ""} onChange={(e) => setShipFields({ ...shipFields, freight_name: e.target.value })} data-testid="bulk-ship-freight-name" />
                                <Input placeholder="Plaka *" value={shipFields.freight_plate || ""} onChange={(e) => setShipFields({ ...shipFields, freight_plate: e.target.value })} data-testid="bulk-ship-freight-plate" />
                                <Input placeholder="İletişim numarası *" value={shipFields.freight_phone || ""} onChange={(e) => setShipFields({ ...shipFields, freight_phone: e.target.value })} data-testid="bulk-ship-freight-phone" />
                            </>
                        )}
                        {shipMethod === "other" && (
                            <Textarea rows={2} placeholder="Açıklama *" value={shipFields.ship_note || ""} onChange={(e) => setShipFields({ ...shipFields, ship_note: e.target.value })} data-testid="bulk-ship-other-note" />
                        )}
                        {shipMethod === "own_vehicle" && <p className="rounded-lg bg-muted p-3 text-xs text-muted-foreground">Firmamız araçları ile gönderim — ek bilgi gerekmez.</p>}
                        <p className="text-xs text-muted-foreground">Gönderim bilgileri seçilen tüm siparişlere uygulanır; geçiş kuralına uymayan siparişler atlanır ve bildirilir.</p>
                    </div>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setShipDlg(false)}>Vazgeç</Button>
                        <Button onClick={() => applyBulk({ ship_method: shipMethod, ...shipFields })} disabled={busy || !shipMethod} className="bg-purple-700 text-white hover:bg-purple-800" data-testid="bulk-ship-confirm-button">
                            {busy ? "Gönderiliyor..." : "Toplu Gönderimi Onayla"}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {!orders ? (
                <div className="space-y-3">{[...Array(4)].map((_, i) => <div key={i} className="h-20 animate-pulse rounded-2xl bg-muted" />)}</div>
            ) : (
                <div className="overflow-hidden rounded-2xl border bg-card shadow-sm">
                    <div className="overflow-x-auto">
                        <table className="w-full text-sm" data-testid="orders-table">
                            <thead>
                                <tr className="border-b bg-muted/60 text-left text-xs uppercase tracking-wider text-muted-foreground">
                                    {isSupplier && (
                                        <th className="p-4">
                                            <input type="checkbox" className="h-4 w-4 accent-terra-600" checked={filtered.length > 0 && selected.size === filtered.length} onChange={toggleAll} data-testid="bulk-select-all" />
                                        </th>
                                    )}
                                    <th className="p-4">Sipariş No</th>
                                    <th className="p-4">{isSupplier ? "Alıcı" : "Tedarikçi"}</th>
                                    <th className="p-4">Kalem</th>
                                    <th className="p-4">Tutar</th>
                                    <th className="p-4">Teslimat</th>
                                    <th className="p-4">Durum</th>
                                    <th className="p-4">Tarih</th>
                                    <th className="p-4 text-right">Kaynak</th>
                                </tr>
                            </thead>
                            <tbody>
                                {filtered.map((o) => (
                                    <tr key={o.order_id} className={`border-b last:border-0 transition-colors hover:bg-muted/40 ${selected.has(o.order_id) ? "bg-terra-50/60" : ""}`} data-testid={`order-row-${o.order_no}`}>
                                        {isSupplier && (
                                            <td className="p-4">
                                                <input type="checkbox" className="h-4 w-4 accent-terra-600" checked={selected.has(o.order_id)} onChange={() => toggle(o.order_id)} data-testid={`bulk-select-${o.order_no}`} />
                                            </td>
                                        )}
                                        <td className="p-4">
                                            <Link to={`/panel/siparis/${o.order_id}`} className="font-semibold text-primary hover:underline" data-testid={`order-link-${o.order_no}`}>{o.order_no}</Link>
                                        </td>
                                        <td className="p-4">{isSupplier ? o.buyer_company_name : o.supplier_name}</td>
                                        <td className="p-4 text-muted-foreground">{o.items.length} kalem</td>
                                        <td className="p-4 font-display font-extrabold text-terra-600">{fmtTL(o.total)}</td>
                                        <td className="p-4">
                                            {o.status !== "cancelled" && (
                                                <div className="flex items-center gap-2" data-testid={`order-progress-${o.order_no}`}>
                                                    <div className="h-1.5 w-16 overflow-hidden rounded-full bg-stone-200">
                                                        <div className="h-full rounded-full bg-emerald-500 transition-all" style={{ width: `${deliveryProgress(o)}%` }} />
                                                    </div>
                                                    <span className="text-[10px] font-semibold text-muted-foreground">%{deliveryProgress(o)}</span>
                                                </div>
                                            )}
                                        </td>
                                        <td className="p-4"><Badge variant="outline" className={`text-[10px] ${ORDER_STATUS[o.status]?.cls}`} data-testid={`order-status-${o.order_no}`}>{ORDER_STATUS[o.status]?.label}</Badge></td>
                                        <td className="p-4 text-muted-foreground">{fmtDate(o.created_at)}</td>
                                        <td className="p-4 text-right text-xs text-muted-foreground">{o.source === "rfq" ? "RFQ" : "Sepet"}</td>
                                    </tr>
                                ))}
                                {!filtered.length && (
                                    <tr><td colSpan={colSpan} className="p-16 text-center text-sm text-muted-foreground" data-testid="orders-empty">Sipariş bulunamadı.</td></tr>
                                )}
                            </tbody>
                        </table>
                    </div>
                </div>
            )}
        </div>
    );
}
